import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Users, Loader2, CheckCircle2, XCircle, Calendar, MapPin, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { BACKEND_URL } from '../lib/api';

const PERMISSION_LABELS = {
  can_scan_tickets: 'Scan tickets at the door',
  can_view_guest_list: 'View the guest list',
  can_check_in_guests: 'Check guests in',
  can_view_basic_event_details: 'View basic event details',
  can_resend_tickets: 'Resend ticket emails',
  can_view_sales_summary: 'View sales summary',
};

/**
 * Public page hit by team-invite emails: GET /team/accept/:token.
 * Resolves the invite, shows event details when scoped, then offers either
 * one-click accept (logged-in matching email) or signup. After accept the
 * associate is redirected to /associate/events/{event_id}/tools when the
 * invite was event-scoped, otherwise to /dashboard.
 */
const AcceptInvitePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    const cleanToken = (token || '').trim();
    if (!cleanToken) {
      setError('This invitation link is missing its token. Please request a new invite from your organiser.');
      setLoading(false);
      return;
    }
    // Preserve invite token across login redirects
    try { sessionStorage.setItem('vp_pending_invite_token', cleanToken); } catch {}
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/team/invite/${encodeURIComponent(cleanToken)}`);
        if (!active) return;
        setInvite(res.data);
      } catch (e) {
        if (!active) return;
        setError(e.response?.data?.detail || 'This invitation is invalid or has expired.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const redirectAfterAccept = (eventId) => {
    // Clear the preserved token so it doesn't leak into other flows
    try { sessionStorage.removeItem('vp_pending_invite_token'); } catch {}
    if (eventId) {
      navigate(`/associate/events/${eventId}/tools`);
    } else {
      navigate('/associate/events');
    }
  };

  const accept = async (payload = {}) => {
    setSubmitting(true);
    try {
      const cleanToken = (token || '').trim();
      const res = await axios.post(
        `${BACKEND_URL}/api/team/invite/${encodeURIComponent(cleanToken)}/accept`,
        payload,
        { withCredentials: true },
      );
      setDone(true);
      if (res.data?.user) setUser?.(res.data.user);
      const headline = invite?.event_title || invite?.org_name;
      toast.success(`You've joined ${headline}.`);
      setTimeout(() => redirectAfterAccept(res.data?.event_id || invite?.event_id), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3D2B]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
        <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10 text-center" data-testid="accept-invite-error">
          <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Invitation Unavailable</h1>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link to="/" className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium">Back to VinePass</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
        <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10 text-center" data-testid="accept-invite-done">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Welcome aboard.</h1>
          <p className="text-stone-600 mb-6">
            You've joined{' '}
            <span className="font-semibold">{invite?.event_title || invite?.org_name}</span>.
          </p>
          <p className="text-xs text-stone-500">Redirecting to your tools…</p>
        </div>
      </div>
    );
  }

  const sameEmailUser = user && user.email?.toLowerCase() === invite?.invitee_email?.toLowerCase();
  const isEventScoped = Boolean(invite?.event_id);
  const heading = isEventScoped ? `Join ${invite?.event_title}` : `Join ${invite?.org_name}`;
  const subheading = isEventScoped
    ? `You've been invited to help manage ${invite?.event_title}`
    : `${invite?.owner_name || 'The organiser'} has invited you to help manage events`;
  const grantedPerms = Object.entries(invite?.permissions || {}).filter(([, v]) => v);

  return (
    <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
      <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#F4F6F3] flex items-center justify-center mb-4">
            {isEventScoped ? <Ticket className="w-7 h-7 text-[#1F3D2B]" /> : <Users className="w-7 h-7 text-[#1F3D2B]" />}
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A27A] mb-1" data-testid="accept-invite-eyebrow">
            {isEventScoped ? 'Event Invitation' : 'Team Invitation'}
          </p>
          <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2" data-testid="accept-invite-heading">
            {heading}
          </h1>
          <p className="text-sm text-stone-500" data-testid="accept-invite-subheading">{subheading}</p>
        </div>

        {isEventScoped && (
          <div className="bg-[#FBF9F4] border border-[#E5D9B6] rounded-xl p-4 mb-5 space-y-1.5" data-testid="accept-invite-event-card">
            <p className="font-serif text-lg text-[#1F3D2B]" data-testid="accept-invite-event-title">{invite?.event_title}</p>
            <p className="text-xs text-[#4A5D50] flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> {invite?.event_date}{invite?.event_time ? ` · ${invite.event_time}` : ''}
            </p>
            {invite?.event_location && (
              <p className="text-xs text-[#4A5D50] flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" /> {invite.event_location}
              </p>
            )}
            <p className="text-xs text-[#4A5D50] mt-2">
              Organiser: <span className="font-medium text-[#112318]">{invite?.org_name}</span>
            </p>
          </div>
        )}

        <p className="text-xs text-stone-500 mb-4 text-center">
          Inviting <strong>{invite?.invitee_email}</strong> as <strong>{invite?.role || 'associate'}</strong>.
        </p>

        {grantedPerms.length > 0 && (
          <div className="border border-stone-200 rounded-lg p-3 mb-5" data-testid="accept-invite-permissions">
            <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1.5">Permissions granted</p>
            <ul className="text-xs text-[#4A5D50] space-y-1">
              {grantedPerms.map(([k]) => (
                <li key={k} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {PERMISSION_LABELS[k] || k}
                </li>
              ))}
            </ul>
          </div>
        )}

        {sameEmailUser ? (
          <Button
            onClick={() => accept({})}
            disabled={submitting}
            data-testid="accept-invite-existing"
            className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-3"
          >
            {submitting ? 'Joining…' : (isEventScoped ? `Accept invitation` : `Join ${invite?.org_name}`)}
          </Button>
        ) : user ? (
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 text-center" data-testid="accept-invite-wrong-user">
            You're logged in as <strong>{user.email}</strong>. This invitation is for <strong>{invite?.invitee_email}</strong>.<br/>
            Please log out and click the link again.
          </p>
        ) : (
          <form
            onSubmit={(e) => { e.preventDefault(); accept({ name, password }); }}
            className="space-y-3"
            data-testid="accept-invite-signup-form"
          >
            <div>
              <Label>Your name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="accept-invite-name" />
            </div>
            <div>
              <Label>Choose a password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required data-testid="accept-invite-password" />
              <p className="text-xs text-stone-500 mt-1">At least 8 characters.</p>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-3"
              data-testid="accept-invite-submit"
            >
              {submitting ? 'Joining…' : 'Create account & join'}
            </Button>
            <p className="text-xs text-stone-500 text-center">
              Already have a VinePass account?{' '}
              <Link to={`/login?next=${encodeURIComponent(`/team/accept/${token}`)}`} className="underline">
                Log in
              </Link>
              , then you'll be returned here to accept.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
